import { createContext, ReactNode, useContext } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import  api from '../services/api';
import LocalStorageService from '../services/storage';

const localStorageService = LocalStorageService();

interface Transaction{
    id_order: string,
    id_transaction: string,
    isactive: boolean,
    product_amount: number,
    unity_price: number,
    total_value: number,
    fk_costs_categories_id_category: string,
    fk_products_id_product: string,
}

type TransactionInput = Omit<Transaction, 'id_transaction'>

interface TransactionsProviderProps {
    //Para aceitar qualquer tipo de conteudo (Children) válido dentro do react
    children: ReactNode;
}

interface ResponseProps {
    success: boolean;
    message: string;
}

interface TransactionsContextData {
    createTransaction: (transaction: TransactionInput) => Promise<ResponseProps>;
    updateTransaction: (transaction: Transaction) => Promise<ResponseProps>;
    updateOnDelete: (id: number) => Promise<ResponseProps>;
}

interface UseTransactionsOptions {
    loadTransactions?: boolean;
}

interface UseTransactionsData extends TransactionsContextData {
    transactions: Transaction[];
    loading: boolean;
}

const TransactionsContext = createContext<TransactionsContextData>(
    // Para burlar o TS forçando a tipagem que a gente quer
    {} as TransactionsContextData
);

const transactionsQueryKey = ['transactions'];
const emptyTransactions: Transaction[] = [];

function hasToken() {
  return Boolean(localStorageService.getIdToken());
}

async function fetchTransactions() {
  const response = await api.get('transactions');
  return Array.isArray(response?.data) ? response.data : [];
}

export function TransactionsProvider({ children } : TransactionsProviderProps) {
  const queryClient = useQueryClient();

  function invalidateTransactions() {
    queryClient.invalidateQueries({ queryKey: transactionsQueryKey });
  }

  async function updateOnDelete (id: number) {
    const Id = { id };
    const response = await api.post('/deletetransactions', Id);
    if (response.status === 200) {
      invalidateTransactions();
      return {
        success: true,
        message: "Transação removida com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao remover transação."
    };
  }

  async function updateTransaction(transaction: Transaction) {
    const response = await api.put('/transactions', transaction);
    if (response.status === 200) {
      invalidateTransactions();
      return {
        success: true,
        message: "Transação editada com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao editar transação."
    };
  }

  async function createTransaction(transactionInput: TransactionInput) {
    const response = await api.post('/transactions', transactionInput);
    if (response.status === 200) {
      invalidateTransactions();
      return {
        success: true,
        message: "Transação adicionada com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao adicionar transação."
    };

  }

  return (
    <TransactionsContext.Provider value={{ createTransaction, updateTransaction, updateOnDelete }}>
      {children}
    </TransactionsContext.Provider>
  );

}

export function UseTransactions(options: UseTransactionsOptions = {}): UseTransactionsData {
  const context = useContext(TransactionsContext);
  const { loadTransactions = false } = options;

  const transactionsQuery = useQuery({
    queryKey: transactionsQueryKey,
    queryFn: fetchTransactions,
    enabled: loadTransactions && hasToken(),
    staleTime: 0
  });

  return {
    ...context,
    transactions: transactionsQuery.data || emptyTransactions,
    loading: loadTransactions ? transactionsQuery.isLoading : false
  };
}
